import React, { useCallback, useEffect, useState } from "react";

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import {
  Edit,
  Close,
} from "@mui/icons-material";

import CustomTextField from "../../../components/common/Textfield";
import apiService from "../../../service/Api";
import useDebounce from "../../../hooks/useDebounce";

import { LocalChecklistProps } from "../Checklists";

type TaskItem = {
  id?: number;
  name: string;
  status: "pending" | "completed";
  points: number; // new field
};

type DraftTask = {
  title: string;
  items: TaskItem[];
  draftItem: string;
  draftPoints: number;
};

export interface RoleItem {
  id: number;
  name: string;
}

interface SearchRoleDialogProps {
  searchRoleOpen: boolean;
  setSearchRoleOpen: (open: boolean) => void;

  searchResults: RoleItem | null;
  setSearchResults: (role: RoleItem | null) => void;

  setAddRoleOpen: (open: boolean) => void;
  setAddTaskOpen: (open: boolean) => void;
  checklist: () => void;
  onAddChecklist: (data: {
    role: RoleItem | null;
    checklistItem: string;
    usage: string | null;
    description: string;
    tasks: {
      title: string;
      task_item: {
        name: string;
        status: "pending" | "completed";
        points?: number;
      }[];
    }[];
  }) => void;

  // NEW PROPS
  mode?: "add" | "edit";

  editChecklist?: LocalChecklistProps | null;
}

export const SearchRoleDialog: React.FC<SearchRoleDialogProps> = ({
  searchRoleOpen,
  setSearchRoleOpen,
  searchResults,
  setSearchResults,
  onAddChecklist,
  mode,
  editChecklist,
  checklist,
}) => {
  const [roleOptions, setRoleOptions] = useState<RoleItem[]>([]);
  const [roleVal, setRoleVal] = useState("");
  const [rolesLoading, setRolesLoading] = useState(false);

  const debouncedRoleSearch = useDebounce(roleVal, 400);
  const [draftTask, setDraftTask] = useState<DraftTask>({
    title: "",
    items: [],
    draftItem: "",
    draftPoints: 0,
  });

  const [savedTasks, setSavedTasks] = useState<
    {
      id?: number; // 👈 SECTION ID
      title: string;
      task_item: TaskItem[];
    }[]
  >([]);

  const addDraftItem = () => {
    if (!draftTask.draftItem.trim()) return;

    setDraftTask((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          name: prev.draftItem,
          status: "pending",
          points: prev.draftPoints || 0,
        },
      ],
      draftItem: "",
      draftPoints: 0, // reset after adding
    }));
  };

  const commitTask = () => {
    if (!draftTask.title.trim() || draftTask.items.length === 0) return;

    setSavedTasks((prev) => [
      ...prev,
      {
        title: draftTask.title,
        task_item: draftTask.items, // points included
      },
    ]);

    setDraftTask({
      title: "",
      items: [],
      draftItem: "",
      draftPoints: 0,
    });
  };

  const [checklistName, setChecklistName] = useState("");
  const [description, setDescription] = useState("");
  const [checklistType, setChecklistType] = useState<string | null>(null);

  const closeAndReset = () => {
    setSearchRoleOpen(false);

    setRoleVal("");
    setRoleOptions([]);
    setSearchResults(null);

    setDraftTask({
      title: "",
      items: [],
      draftItem: "",
      draftPoints: 0,
    });
    setSavedTasks([]);
  };

  useEffect(() => {
    if (searchRoleOpen) {
      setRoleVal("");
      setRoleOptions([]);
      setSearchResults(null);

      setDraftTask({
        title: "",
        items: [],
        draftItem: "",
        draftPoints: 0,
      });
      setSavedTasks([]);
    }
  }, [searchRoleOpen, setSearchResults]);

  const resolveRoleByName = useCallback(
    async (roleName: string) => {
      try {
        const res = await apiService.get<{ results: RoleItem[] }>(
          "/api/crews/roles/",
          { search: roleName, page_size: 10 }
        );

        const matched = res.data.results.find(
          (r) => r.name.toLowerCase() === roleName.toLowerCase()
        );

        if (matched) {
          setSearchResults(matched); // ✅ real id
        }
      } catch (err) {
        console.error("Failed to resolve role:", err);
      }
    },
    [setSearchResults]
  );

  const fetchRoles = useCallback(async (searching: string) => {
    if (!searching) {
      setRoleOptions([]);
      return;
    }
    setRolesLoading(true);
    try {
      const roles = await apiService.get<{ results: RoleItem[] }>(
        "/api/crews/roles/",
        { search: searching, page_size: 10 }
      );
      setRoleOptions(roles.data.results || []);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
      setRoleOptions([]);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles(debouncedRoleSearch);
  }, [debouncedRoleSearch, fetchRoles]);

  const handleSaveChecklist = () => {
    onAddChecklist({
      role: searchResults,
      checklistItem: checklistName,
      usage: checklistType,
      description,
      tasks: savedTasks,
    });

    closeAndReset();
  };
  const handleUpdateChecklist = async () => {
    if (!editChecklist) return;

    try {
      // Prepare payload
      const payload = {
        name: checklistName,
        description,
        roles: searchResults?.id || null,
        notes: "", // add any notes field if needed
        sections: savedTasks.map((task, sectionIndex) => ({
          id: task.id, // 👈 tells backend “update this section”
          name: task.title,
          description: "",
          order: sectionIndex,
          list_items: task.task_item.map((item, itemIndex) => ({
            id: item.id, // 👈 tells backend “update this item”
            name: item.name,
            points: item.points,
            status: item.status,
            order: itemIndex,
          })),
        })),
      };

      // Send PATCH request
      await apiService.patch(
        `/api/checklists/checklist/${editChecklist.id}/`,
        payload
      );

      // Reset the form and close dialog
      closeAndReset();
      checklist();
    } catch (err) {
      console.error("Failed to update checklist:", err);
    }
  };

  useEffect(() => {
    if (searchRoleOpen) {
      if (mode === "edit" && editChecklist) {
        // Prefill for edit mode
        setChecklistName(editChecklist.title);
        setDescription(editChecklist.type);
        resolveRoleByName(editChecklist.role);

        setSavedTasks(
          editChecklist.tasks.map((task) => ({
            id: task.id, // 👈 preserve section id
            title: task.title,
            task_item: task.task_item.map((item) => ({
              id: item.id, // 👈 preserve item id
              name: item.name,
              status: item.status,
              points: item.points ?? 0,
            })),
          }))
        );

        setDraftTask({
          title: "",
          items: [],
          draftItem: "",
          draftPoints: 0,
        });
      } else {
        // Add mode – reset everything
        setChecklistName("");
        setDescription("");
        setSearchResults(null);
        setSavedTasks([]);
        setDraftTask({
          title: "",
          items: [],
          draftItem: "",
          draftPoints: 0,
        });
      }
    }
  }, [editChecklist, mode, resolveRoleByName, searchRoleOpen, setSearchResults]);

  return (
    <Dialog
      open={searchRoleOpen}
      onClose={closeAndReset}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 420,
          maxWidth: "90%",
        },
      }}
    >
      <DialogTitle>
        {mode === "edit" ? "Edit Checklist" : "Add Checklist"}
        <IconButton
          onClick={closeAndReset}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Checklist Name */}
        <Typography variant="subtitle2" sx={{ mt: 3 }}>
          Checklist Name
        </Typography>
        <CustomTextField
          placeholder="eg. Camera"
          value={checklistName}
          onChange={(e) => setChecklistName(e.target.value)}
          sx={{ mt: 1 }}
        />

        {/* Description */}

        <Typography variant="subtitle2" sx={{ mt: 3 }}>
          Description
        </Typography>
        <CustomTextField
          placeholder="Add context for crew member"
          multiline
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mt: 2 }}
        />

        <Typography variant="subtitle2" sx={{ mt: 3 }}>
          Assign To Role
        </Typography>

        <Autocomplete
          multiple={false}
          options={roleOptions}
          getOptionLabel={(option) => option.name || "Unnamed Role"}
          value={searchResults}
          onChange={(_, newValue) => setSearchResults(newValue)}
          inputValue={roleVal}
          onInputChange={(_, newInput) => setRoleVal(newInput)}
          loading={rolesLoading}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              placeholder="Search roles..."
              variant="outlined"
              helperText="Type 3 letters to search"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {rolesLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mt: 1 }}
        />

        <Autocomplete
          options={["Pre-stream", "On-stream", "Post-stream"]}
          value={checklistType}
          onChange={(_, v) => setChecklistType(v)}
          renderInput={(params) => (
            <CustomTextField {...params} label="Checklist Type" />
          )}
          sx={{ mt: 2 }}
        />

        {/* ---------------- TASK SECTION ---------------- */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
            mt: 3,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Task Section
          </Typography>

          {/* Task Title */}
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Task Title
          </Typography>
          <CustomTextField
            fullWidth
            placeholder="e.g. Tripod Setup"
            value={draftTask.title}
            onChange={(e) =>
              setDraftTask((prev) => ({ ...prev, title: e.target.value }))
            }
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Add Task Item
          </Typography>

          {/* Task Item */}
          <CustomTextField
            fullWidth
            placeholder="Task item (e.g. Mount tripod)"
            value={draftTask.draftItem}
            onChange={(e) =>
              setDraftTask((prev) => ({
                ...prev,
                draftItem: e.target.value,
              }))
            }
            sx={{ mb: 2 }}
          />

          {/* Points */}
          <CustomTextField
            type="number"
            fullWidth
            placeholder="Points"
            value={draftTask.draftPoints || ""}
            onChange={(e) =>
              setDraftTask((prev) => ({
                ...prev,
                draftPoints: parseInt(e.target.value) || 0,
              }))
            }
            sx={{ mb: 2 }}
          />

          {/* Add Button */}
          <Button
            variant="contained"
            onClick={addDraftItem}
            disabled={!draftTask.draftItem.trim()}
            fullWidth
          >
            Add Task Item
          </Button>

          {/* Draft Items Preview */}
          {draftTask.items.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {draftTask.title || "Task Preview"}
              </Typography>

              {draftTask.items.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1.5,
                    mb: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.default",
                  }}
                >
                  <Typography variant="body2">
                    {item.name} • {item.points} pts
                  </Typography>

                  <Box>
                    {/* Edit Button */}
                    <IconButton
                      size="small"
                      onClick={() =>
                        setDraftTask((prev) => ({
                          ...prev,
                          draftItem: item.name,
                          draftPoints: item.points,
                          items: prev.items.filter((_, i) => i !== idx), // remove from draft while editing
                        }))
                      }
                    >
                      <Edit fontSize="small" />
                    </IconButton>

                    {/* Delete Button */}
                    <IconButton
                      size="small"
                      onClick={() =>
                        setDraftTask((prev) => ({
                          ...prev,
                          items: prev.items.filter((_, i) => i !== idx),
                        }))
                      }
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Commit Task */}
          <Button
            variant="outlined"
            sx={{ mt: 3 }}
            onClick={commitTask}
            disabled={!draftTask.title || draftTask.items.length === 0}
            fullWidth
          >
            Save Task
          </Button>
        </Box>

        {/* ---------------- SAVED TASKS SECTION ---------------- */}
        {savedTasks.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Saved Tasks
            </Typography>
            {savedTasks.map((task, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography fontWeight={600}>{task.title}</Typography>

                  <Box>
                    {/* Edit task */}
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDraftTask({
                          title: task.title,
                          items: task.task_item,
                          draftItem: "",
                          draftPoints: 0,
                        });
                        // Remove from savedTasks temporarily to avoid duplicates
                        setSavedTasks((prev) =>
                          prev.filter((_, i) => i !== idx)
                        );
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>

                    {/* Delete task */}
                    <IconButton
                      size="small"
                      onClick={() =>
                        setSavedTasks((prev) =>
                          prev.filter((_, i) => i !== idx)
                        )
                      }
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ pl: 2, mt: 1 }}>
                  {task.task_item.map((item, i) => (
                    <Typography key={i} variant="body2">
                      • {item.name} ({item.points} pts)
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <DialogActions>
          <Button onClick={closeAndReset}>Cancel</Button>
          <Button
            variant="contained"
            onClick={
              mode === "edit" ? handleUpdateChecklist : handleSaveChecklist
            }
          >
            {mode === "edit" ? "Save Changes" : "Add Checklist"}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default SearchRoleDialog;
